import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../axiosBaseQuery';
import companiesCustomerApiParser, {
  type CompaniesCustomerData,
} from './companiesCustomerApiParser';

// ---------- Types ----------
export interface CompaniesCustomer {
  id: string | number;
  srno: number;
  shortName: string;
  customer: string;
  status: string;
  actions: string;
}

export interface CompaniesCustomerListResponse {
  data: CompaniesCustomer[];
}

interface CreateCompaniesCustomerPayload {
  companyId: number;
  shortName: string;
  active: boolean;
}

export interface GetCompaniesCustomerListParams {
  [key: string]: any;
}

// ---------- API Slice ----------
const companiesCustomerApi = createApi({
  reducerPath: 'companiesCustomerApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['CompaniesCustomer', 'CompaniesCustomerData'],
  endpoints: (builder) => ({
    getCompaniesCustomerList: builder.query<CompaniesCustomer[], any>({
      query: (params) => ({
        url: 'lubricant/customers',
        method: 'GET',
        params: params,
      }),
      transformResponse: (res: CompaniesCustomerListResponse | any): CompaniesCustomer[] =>
        companiesCustomerApiParser.CompaniesCustomerListParser(res),
    }),

    getCompaniesCustomerData: builder.query<CompaniesCustomerData, void>({
      query: () => ({
        url: '/lubricant/companies/short?partner=customer',
        method: 'GET',
      }),
      transformResponse: (res: any): CompaniesCustomerData => {
        const content = Array.isArray(res?.data?.content) ? res.data.content : [];
        const companies = content.map((c: any) => ({ value: String(c.id), label: String(c.name) }));
        return {
          companiesCustomers: [],
          companies,
          statusOptions: [
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' },
          ],
        } as CompaniesCustomerData;
      },
      providesTags: ['CompaniesCustomerData'],
    }),

    createCompaniesCustomer: builder.mutation<any, CreateCompaniesCustomerPayload>({
      query: (payload) => ({
        url: '/lubricant/customers',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['CompaniesCustomer', 'CompaniesCustomerData'],
    }),

    updateCompaniesCustomer: builder.mutation<
      any,
      { id: number; payload: { shortName: string; active: boolean } }
    >({
      query: ({ id, payload }) => ({
        url: `/lubricant/customers/${encodeURIComponent(id)}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CompaniesCustomer' as const, id: id.toString() },
        'CompaniesCustomer',
        'CompaniesCustomerData',
      ],
    }),

    deleteCompaniesCustomer: builder.mutation<any, number>({
      query: (id) => ({
        url: `/lubricant/customers/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CompaniesCustomer', 'CompaniesCustomerData'],
    }),
  }),
});

// ---------- Exports ----------
export const companiesCustomerReducer = companiesCustomerApi.reducer;
export const companiesCustomerMiddleware = companiesCustomerApi.middleware;
export const companiesCustomerReducerPath = companiesCustomerApi.reducerPath;

export const {
  useGetCompaniesCustomerListQuery,
  useGetCompaniesCustomerDataQuery,
  useCreateCompaniesCustomerMutation,
  useUpdateCompaniesCustomerMutation,
  useDeleteCompaniesCustomerMutation,
} = companiesCustomerApi;
